import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/common/Layout';

// Auth Components
import LoginForm from './components/auth/LoginForm';
import Register from './components/auth/Register';
import ForgotPasswordForm from './components/auth/ForgotPasswordForm';
import ForgotPasswordSuccess from './components/auth/ForgotPasswordSuccess';
import ResetPasswordForm from './components/auth/ResetPasswordForm';

// Dashboard Components
import AdminDashboard from './components/dashboard/AdminDashboard';
import { CounselorDashboard } from './components/dashboard/CounselorDashboard';
import { StudentDashboard } from './components/dashboard/StudentDashboard';
import { StaffDashboard } from './components/dashboard/StaffDashboard';
import { ReceptionistDashboard } from './components/dashboard/ReceptionistDashboard';
import HodDashboard from './components/dashboard/hod-dashboard/HodDashboard';

// User Components
import { Users } from './components/users/Users';
import { UserProfile } from './components/users/UserProfile';
import UserManagementList from './components/users/management/UserManagementList';

// Appointment Components
import { Appointments } from './components/appointments/Appointments';
import { AppointmentDetails } from './components/appointments/AppointmentDetails';
import { BookAppointment } from './components/appointments/BookAppointment';

// Counseling Components
import { SessionHistory } from './components/counseling/SessionHistory';
import { CounselingSession } from './components/counseling/session';

// Common Components
import { Loading } from './components/common/Loading';
import { NotFound } from './components/common/NotFound';
import Home from './components/home/Home';
import CalendarEvents from './components/calendar-events/CalendarEvents';
import { AnnouncementManagement } from './components/announcements';
import CounselingRecordForm from './components/counseling/forms/CounselingRecordForm';
import SafetyContract from './components/counseling/SafetyContract';
import TerminationForm from './components/counseling/forms/TerminationForm';
import BlockCounselorTimeSlot from './components/dashboard/receptionist/components/ScheduleManagement';
import ClientSessions from './components/counseling/ClientSessions';
import SessionsRouter from './components/counseling/SessionRouter';

// Legal Components
import PrivacyPolicy from './components/legal/PrivacyPolicy';
import TermsOfUse from './components/legal/TermsOfUse';

const PublicRoute = ({ children }) => {
  const { user, loading, isInitialized } = useAuth();
  const location = useLocation();

  // Show loading spinner while auth is initializing
  if (!isInitialized) {
    return <Loading />;
  }


  // Redirect authenticated users to their dashboard
  if (user) {
    const roleRoutes = {
      admin: '/admin/dashboard',
      counselor: '/counselor/dashboard',
      student: '/student/dashboard',
      alumni: '/student/dashboard',
      receptionist: '/receptionist/dashboard',
      staff: '/staff/dashboard',
      faculty: '/staff/dashboard',
      hod: '/hod/dashboard',
    };

    const targetRoute = roleRoutes[user.role] || '/login';
    return <Navigate to={targetRoute} state={{ from: location }} replace />;
  }

  return children;
};

const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { user, loading, isInitialized } = useAuth();
  const location = useLocation();

  // Show loading spinner while auth is initializing
  if (!isInitialized) {
    return <Loading />;
  }

  // Redirect unauthenticated users to login
  if (!user) {
    return (
      <Navigate
        to="/login"
        state={{
          from: location,
          message: 'Please login to access this page'
        }}
        replace
      />
    );
  }

  // Check role authorization
  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    const roleRoutes = {
      admin: '/admin/dashboard',
      counselor: '/counselor/dashboard',
      student: '/student/dashboard',
      alumni: '/student/dashboard',
      receptionist: '/receptionist/dashboard',
      staff: '/staff/dashboard',
      faculty: '/staff/dashboard',
      hod: '/hod/dashboard',
    };

    const targetRoute = roleRoutes[user.role] || '/login';
    return (
      <Navigate
        to={targetRoute}
        state={{
          from: location,
          message: 'You do not have permission to access this page'
        }}
        replace
      />
    );
  }

  return <Layout>{children}</Layout>;
};

const App = () => {
  return (
    <Router>
      <AuthProvider>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={
            <PublicRoute>
              <Home />
            </PublicRoute>
          } />
          <Route path="/login" element={
            <PublicRoute>
              <LoginForm />
            </PublicRoute>
          } />
          <Route path="/register" element={
            <PublicRoute>
              <Register />
            </PublicRoute>
          } />
          <Route path="/forgot-password" element={
            <PublicRoute>
              <ForgotPasswordForm />
            </PublicRoute>
          } />
          <Route path="/forgot-password/success" element={
            <PublicRoute>
              <ForgotPasswordSuccess />
            </PublicRoute>
          } />
          <Route path="/reset-password/:resetToken" element={
            <PublicRoute>
              <ResetPasswordForm />
            </PublicRoute>
          } />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-use" element={<TermsOfUse />} />

          {/* Admin Routes */}
          <Route path="/admin/*" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Routes>
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="users/manage" element={<UserManagementList />} />
                <Route path="users" element={<Users />} />
                <Route path="users/:userId" element={<UserProfile />} />
                <Route path="appointments" element={<Appointments />} />
                <Route path="appointments/new" element={<BookAppointment />} />
                <Route path="appointments/:id" element={<AppointmentDetails />} />
                <Route path="calendar-events" element={<CalendarEvents />} />
                <Route path="announcements" element={<AnnouncementManagement />} />
                <Route path="sessions" element={<SessionHistory />} />
                <Route path="profile" element={<UserProfile />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </ProtectedRoute>
          } />

          {/* Counselor Routes */}
          <Route path="/counselor/*" element={
            <ProtectedRoute allowedRoles={['counselor']}>
              <Routes>
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="dashboard" element={<CounselorDashboard />} />
                <Route path="appointments" element={<Appointments />} />
                <Route path="appointments/new" element={<BookAppointment />} />
                <Route path="appointments/:id" element={<AppointmentDetails />} />

                {/* Sessions list shown with ClientSessions component */}
                <Route path="sessions" element={<ClientSessions />} />

                {/* Individual session routes */}
                <Route path="sessions/:sessionId/record" element={<CounselingRecordForm />} />
                <Route path="sessions/:appointmentId" element={<CounselingSession />} />

                {/* Other routes */}
                <Route path="profile" element={<UserProfile />} />
                <Route path="*" element={<NotFound />} />
                {/* <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="dashboard" element={<CounselorDashboard />} />
                <Route path="appointments" element={<Appointments />} />
                <Route path="appointments/new" element={<BookAppointment />} />
                <Route path="appointments/:id" element={<AppointmentDetails />} />
                <Route path="sessions" element={<ClientSessions />} />
                <Route path="sessions/:appointmentId" element={<CounselingSession />} />
                <Route path="/sessions/:sessionId/create-record" element={<CounselingRecordForm />} />
                <Route path="profile" element={<UserProfile />} />
                <Route path="*" element={<NotFound />} /> */}
              </Routes>
            </ProtectedRoute>
          } />

          {/* Student Routes */}
          <Route path="/student/*" element={
            <ProtectedRoute allowedRoles={['student', 'alumni']}>
              <Routes>
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="dashboard" element={<StudentDashboard />} />
                <Route path="appointments" element={<Appointments />} />
                <Route path="appointments/new" element={<BookAppointment />} />
                <Route path="appointments/:id" element={<AppointmentDetails />} />
                <Route path="sessions" element={<SessionHistory />} />
                <Route path="profile" element={<UserProfile />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </ProtectedRoute>
          } />

          {/* Staff Routes */}
          <Route path="/staff/*" element={
            <ProtectedRoute allowedRoles={['staff', 'faculty']}>
              <Routes>
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="dashboard" element={<StaffDashboard />} />
                <Route path="appointments" element={<Appointments />} />
                <Route path="appointments/new" element={<BookAppointment />} />
                <Route path="appointments/:id" element={<AppointmentDetails />} />
                <Route path="sessions" element={<SessionHistory />} />
                <Route path="profile" element={<UserProfile />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </ProtectedRoute>
          } />

          {/* Head of dept routes */}
          <Route path="/hod/*" element={
            <ProtectedRoute allowedRoles={['hod']}>
              <Routes>
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="dashboard" element={<HodDashboard />} />
                <Route path="users/:userId" element={<UserProfile />} />
                <Route path="appointments" element={<Appointments />} />
                <Route path="appointments/new" element={<BookAppointment />} />
                <Route path="appointments/:id" element={<AppointmentDetails />} />
                <Route path="sessions" element={<ClientSessions />} />
                <Route path="sessions/:appointmentId" element={<CounselingSession />} />
                <Route path="sessions/:sessionId/record" element={<CounselingRecordForm />} />
                <Route path="announcements" element={<AnnouncementManagement />} />
                <Route path="profile" element={<UserProfile />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </ProtectedRoute>
          } />

          {/* Receptionist Routes */}
          <Route path="/receptionist/*" element={
            <ProtectedRoute allowedRoles={['receptionist']}>
              <Routes>
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="dashboard" element={<ReceptionistDashboard />} />
                <Route path="users/manage" element={<UserManagementList />} />
                <Route path="users" element={<Users />} />
                <Route path="users/:userId" element={<UserProfile />} />
                <Route path="appointments" element={<Appointments />} />
                <Route path="appointments/new" element={<BookAppointment />} />
                <Route path="appointments/urgent/new" element={<BookAppointment isUrgent={true} />} />
                <Route path="appointments/:id" element={<AppointmentDetails />} />
                <Route path="calendar-events" element={<CalendarEvents />} />
                <Route path="announcements" element={<AnnouncementManagement />} />
                <Route path="schedules" element={<BlockCounselorTimeSlot />} />
                <Route path="profile" element={<UserProfile />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </ProtectedRoute>
          } />

          {/* Default Routes */}
          <Route path="/" element={<Home />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </AuthProvider>
    </Router>
  );
};

export default App;